import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import OutreachHealthBadge from "@/components/OutreachHealthBadge";
import PriorityBar from "@/components/PriorityBar";
import BrokerDetailSheet from "@/components/BrokerDetailSheet";
import AddBrokerForm from "@/components/AddBrokerForm";
import ExportCsvButton from "@/components/ExportCsvButton";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
    Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Search, Plus, SlidersHorizontal } from "lucide-react";
import { fmtDateShort } from "@/lib/formatDate";

const STATUSES = ["Target", "Outreach", "Connected", "Active", "Nurture"];
const TIERS = [1, 2, 3];
const HEALTHS = ["On Track", "Due Soon", "Overdue"];

export default function BrokerTracker() {
    const [brokers, setBrokers] = useState([]);
    const [search, setSearch] = useState("");
    const [statusF, setStatusF] = useState("all");
    const [tierF, setTierF] = useState("all");
    const [healthF, setHealthF] = useState("all");
    const [ownerF, setOwnerF] = useState("all");
    const [locationF, setLocationF] = useState("");
    const [active, setActive] = useState(null);
    const [open, setOpen] = useState(false);
    const [addOpen, setAddOpen] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();

    const load = () => api.get("/brokers").then((r) => setBrokers(r.data)).catch(() => {});
    useEffect(() => { load(); }, []);

    useEffect(() => {
        const id = searchParams.get("broker");
        if (id && brokers.length) {
            const b = brokers.find((x) => x.id === id);
            if (b) { setActive(b); setOpen(true); }
        }
        if (searchParams.get("add") === "1") {
            setAddOpen(true);
        }
    }, [searchParams, brokers]);

    const closeAddDialog = () => {
        setAddOpen(false);
        if (searchParams.get("add")) {
            const next = new URLSearchParams(searchParams);
            next.delete("add");
            setSearchParams(next, { replace: true });
        }
    };

    const filtered = useMemo(() => {
        return brokers
            .filter((b) => {
                if (search && !`${b.agency_name} ${b.producer_name}`.toLowerCase().includes(search.toLowerCase())) return false;
                if (statusF !== "all" && b.status !== statusF) return false;
                if (tierF !== "all" && b.tier !== parseInt(tierF, 10)) return false;
                if (healthF !== "all" && b.outreach_health !== healthF) return false;
                if (ownerF === "__unassigned") {
                    if (b.owner && b.owner.trim()) return false;
                } else if (ownerF !== "all" && (b.owner || "") !== ownerF) {
                    return false;
                }
                if (locationF && !(b.location || "").toLowerCase().includes(locationF.toLowerCase())) return false;
                return true;
            })
            .sort((a, b) => (b.priority_score || 0) - (a.priority_score || 0));
    }, [brokers, search, statusF, tierF, healthF, ownerF, locationF]);

    // Distinct owner values present in the data, alphabetized
    const ownerOptions = useMemo(() => {
        const set = new Set();
        brokers.forEach((b) => { if (b.owner && b.owner.trim()) set.add(b.owner.trim()); });
        return Array.from(set).sort((a, b) => a.localeCompare(b));
    }, [brokers]);

    const openBroker = (b) => { setActive(b); setOpen(true); setSearchParams({ broker: b.id }); };
    const closeSheet = (o) => {
        setOpen(o);
        if (!o) setSearchParams({});
    };

    const fmt = (d) => fmtDateShort(d);
    const fmtMoney = (n) => !n ? "—" : (n >= 1000000 ? `$${(n / 1000000).toFixed(1)}M` : n >= 1000 ? `$${(n / 1000).toFixed(0)}K` : `$${n}`);

    return (
        <div className="p-6 lg:p-8 space-y-5 animate-fade-in" data-testid="broker-tracker-page">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                <div>
                    <h1 className="ph-h1">Broker tracker</h1>
                    <p className="text-sm text-ph-muted mt-1">Sortable, filterable list of every broker in pipeline.</p>
                </div>
                <div className="flex items-center gap-2">
                    <ExportCsvButton path="/brokers/export" filenamePrefix="players-health-brokers" label="Export all brokers" testid="export-brokers-btn" />
                    <Button
                        onClick={() => setAddOpen(true)}
                        className="bg-ph-primary text-white font-medium h-9 px-3 hover:bg-ph-primaryHover"
                        data-testid="add-broker-btn"
                    >
                        <Plus className="w-4 h-4 mr-1.5" /> Add broker
                    </Button>
                </div>
            </div>

            {/* Filters */}
            <div className="ph-card p-4">
                <div className="flex items-center gap-2 mb-3">
                    <SlidersHorizontal className="w-3.5 h-3.5 text-ph-muted" />
                    <span className="text-xs font-medium text-ph-text">Filters</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
                    <div className="relative md:col-span-2">
                        <Search className="absolute left-3 top-3 w-4 h-4 text-ph-mutedSoft" />
                        <Input
                            placeholder="Search agency or producer…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="pl-9 h-10"
                            data-testid="broker-search-input"
                        />
                    </div>
                    <Select value={statusF} onValueChange={setStatusF}>
                        <SelectTrigger className="h-10" data-testid="filter-status-trigger"><SelectValue placeholder="Status" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All statuses</SelectItem>
                            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Select value={tierF} onValueChange={setTierF}>
                        <SelectTrigger className="h-10" data-testid="filter-tier-trigger"><SelectValue placeholder="Tier" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All tiers</SelectItem>
                            {TIERS.map((t) => <SelectItem key={t} value={String(t)}>Tier {t}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Select value={healthF} onValueChange={setHealthF}>
                        <SelectTrigger className="h-10" data-testid="filter-health-trigger"><SelectValue placeholder="Health" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All health</SelectItem>
                            {HEALTHS.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Select value={ownerF} onValueChange={setOwnerF}>
                        <SelectTrigger className="h-10" data-testid="filter-owner-trigger"><SelectValue placeholder="Owner" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All owners</SelectItem>
                            {ownerOptions.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                            <SelectItem value="__unassigned">Unassigned</SelectItem>
                        </SelectContent>
                    </Select>
                    <Input
                        placeholder="Location filter…"
                        value={locationF}
                        onChange={(e) => setLocationF(e.target.value)}
                        className="h-10 md:col-span-6"
                        data-testid="filter-location-input"
                    />
                </div>
            </div>

            {/* Table */}
            <div className="ph-card overflow-hidden">
                <div className="max-h-[calc(100vh-300px)] overflow-auto scrollbar-thin">
                    <table className="w-full text-sm" data-testid="broker-table">
                        <thead className="bg-ph-canvas text-[11px] font-medium text-ph-muted sticky top-0 z-10 border-b border-ph-border">
                            <tr>
                                <th className="text-left px-4 py-2.5">Agency</th>
                                <th className="text-left px-4 py-2.5">Producer</th>
                                <th className="text-left px-4 py-2.5">Owner</th>
                                <th className="text-left px-4 py-2.5">Location</th>
                                <th className="text-left px-4 py-2.5">Tier</th>
                                <th className="text-left px-4 py-2.5">Status</th>
                                <th className="text-right px-4 py-2.5">Annual $</th>
                                <th className="text-right px-4 py-2.5">Subs</th>
                                <th className="text-left px-4 py-2.5">Last Contact</th>
                                <th className="text-left px-4 py-2.5">Next Due</th>
                                <th className="text-left px-4 py-2.5">Health</th>
                                <th className="text-left px-4 py-2.5">Priority</th>
                                <th className="text-left px-4 py-2.5">Next Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.length === 0 && (
                                <tr><td colSpan={13} className="text-center py-12 text-ph-muted">
                                    Add your first target broker to get started.
                                </td></tr>
                            )}
                            {filtered.map((b) => (
                                <tr
                                    key={b.id}
                                    onClick={() => openBroker(b)}
                                    data-testid={`broker-row-${b.id}`}
                                    className={`border-t border-ph-borderSoft cursor-pointer hover:bg-ph-canvas transition-colors ${
                                        b.outreach_health === "Overdue" ? "border-l-2 border-l-rose-400 bg-rose-50/30" : ""
                                    }`}
                                >
                                    <td className="px-4 py-3 font-medium text-ph-text">{b.agency_name}</td>
                                    <td className="px-4 py-3 text-ph-muted">{b.producer_name}</td>
                                    <td className="px-4 py-3">
                                        {b.owner ? (
                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-ph-primarySoft text-ph-primary text-[11px] font-medium" data-testid={`owner-chip-${b.id}`}>
                                                {b.owner}
                                            </span>
                                        ) : (
                                            <span className="text-[11px] text-ph-mutedSoft">—</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-ph-muted">{b.location}</td>
                                    <td className="px-4 py-3">
                                        <span className="inline-flex px-1.5 py-0.5 rounded-md bg-ph-canvas border border-ph-border text-[11px] font-medium text-ph-muted">T{b.tier}</span>
                                    </td>
                                    <td className="px-4 py-3"><StatusBadge status={b.status} /></td>
                                    <td className="px-4 py-3 text-right tabular-nums text-ph-text">{fmtMoney(b.estimated_annual_premium)}</td>
                                    <td className="px-4 py-3 text-right tabular-nums text-ph-text">{b.submission_count || 0}</td>
                                    <td className="px-4 py-3 text-ph-muted">{fmt(b.last_contact_date)}</td>
                                    <td className="px-4 py-3 text-ph-muted">{fmt(b.next_followup_due)}</td>
                                    <td className="px-4 py-3"><OutreachHealthBadge value={b.outreach_health} /></td>
                                    <td className="px-4 py-3 min-w-[90px]"><PriorityBar score={b.priority_score} /></td>
                                    <td className="px-4 py-3 text-xs text-ph-primary">{b.next_action || "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className="px-4 py-2.5 text-xs text-ph-muted border-t border-ph-border bg-ph-canvas">
                    Showing <span className="font-medium text-ph-text">{filtered.length}</span> of {brokers.length} brokers
                </div>
            </div>

            <BrokerDetailSheet
                open={open}
                onOpenChange={closeSheet}
                broker={active}
                onUpdated={(b) => {
                    setActive(b);
                    setBrokers((prev) => prev.map((x) => x.id === b.id ? b : x));
                }}
                onDeleted={(deletedId) => {
                    setBrokers((prev) => prev.filter((x) => x.id !== deletedId));
                    setActive(null);
                }}
            />

            <Dialog open={addOpen} onOpenChange={(o) => (o ? setAddOpen(true) : closeAddDialog())}>
                <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto scrollbar-thin" data-testid="add-broker-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-lg font-semibold text-ph-text">Add broker</DialogTitle>
                        <DialogDescription className="text-sm text-ph-muted">
                            Required fields are marked with *.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="pt-2">
                        <AddBrokerForm
                            onSuccess={() => {
                                closeAddDialog();
                                load();
                            }}
                            onCancel={closeAddDialog}
                        />
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
